import {
  formatReceiptDateTime,
  formatReceiptMoney,
  receiptLineDescriptions,
} from '@/components/pos/posReceipt'
import {
  formatTaxInvoiceNumber,
  getTaxInvoiceGuestLabel,
} from '@/components/pos/posTaxInvoiceUtils'

/**
 * @param {object} props
 * @param {import('@/types/api').PosTaxInvoice | null} props.invoice
 */
export function PosTaxInvoice({ invoice }) {
  if (!invoice) return null

  const currency = invoice.invoice?.currency_code ?? 'AED'

  return (
    <div className="tax-invoice-root pointer-events-none fixed -left-[9999px] top-0">
      <article className="tax-invoice" aria-label="Tax invoice">
        <header className="tax-invoice__header">
          <div>
            <p className="tax-invoice__title">{invoice.title ?? 'Tax Invoice'}</p>
            <p className="tax-invoice__legal-name">{invoice.company?.legal_name}</p>
            <p>{invoice.branch?.name}</p>
            {invoice.branch?.address ? <p>{invoice.branch.address}</p> : null}
            {invoice.branch?.phone ? <p>Tel: {invoice.branch.phone}</p> : null}
            {invoice.branch?.trn ? <p className="tax-invoice__trn">TRN: {invoice.branch.trn}</p> : null}
          </div>
          <div className="tax-invoice__meta-box">
            <p><strong>Invoice #</strong> {formatTaxInvoiceNumber(invoice)}</p>
            <p><strong>Date</strong> {formatReceiptDateTime(invoice.invoice?.issued_at)}</p>
            <p><strong>Customer</strong> {getTaxInvoiceGuestLabel(invoice)}</p>
            {invoice.customer?.phone ? <p><strong>Phone</strong> {invoice.customer.phone}</p> : null}
          </div>
        </header>

        <table className="tax-invoice__table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Net</th>
              <th>{invoice.tax_label ?? 'VAT'}</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines ?? []).map((line, index) => {
              const descriptions = receiptLineDescriptions(line)

              return (
              <tr key={`${line.description}-${index}`} className={line.is_addon ? 'tax-invoice__addon-row' : ''}>
                <td>
                  {line.is_addon ? '+ ' : ''}
                  <span>{descriptions[0].text}</span>
                  {descriptions[1] ? (
                    <span className="tax-invoice__line-ar" dir="rtl">{descriptions[1].text}</span>
                  ) : null}
                </td>
                <td>{line.quantity}</td>
                <td>{formatReceiptMoney(line.unit_price, currency)}</td>
                <td>{formatReceiptMoney(line.net_amount, currency)}</td>
                <td>{formatReceiptMoney(line.tax_amount, currency)}</td>
                <td>{formatReceiptMoney(line.line_total, currency)}</td>
              </tr>
              )
            })}
          </tbody>
        </table>

        <section className="tax-invoice__summary">
          <div className="tax-invoice__summary-grid">
            <Row label="Taxable amount" value={formatReceiptMoney(invoice.tax_summary?.taxable_amount, currency)} />
            <Row
              label={`${invoice.tax_label ?? 'VAT'} (${invoice.tax_summary?.vat_rate_percent ?? 5}%)`}
              value={formatReceiptMoney(invoice.tax_summary?.vat_amount, currency)}
            />
            {Number(invoice.totals?.discount) > 0 ? (
              <Row label="Discount" value={`-${formatReceiptMoney(invoice.totals.discount, currency)}`} />
            ) : null}
            {Number(invoice.totals?.tip) > 0 ? (
              <Row label="Tip" value={formatReceiptMoney(invoice.totals.tip, currency)} />
            ) : null}
            <Row label="Total (inc. VAT)" value={formatReceiptMoney(invoice.totals?.total, currency)} bold />
          </div>
          {Array.isArray(invoice.tax_summary?.vat_breakdown) && invoice.tax_summary.vat_breakdown.length > 0 ? (
            <div className="mt-3">
              <p className="tax-invoice__section-title">VAT breakdown</p>
              <table className="tax-invoice__table">
                <thead>
                  <tr>
                    <th>Rate</th>
                    <th>Net</th>
                    <th>VAT</th>
                    <th>Gross</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.tax_summary.vat_breakdown.map((row) => (
                    <tr key={`${row.tax_profile_name}-${row.rate_percent}`}>
                      <td>
                        {row.tax_profile_name} ({row.rate_percent}%)
                      </td>
                      <td>{formatReceiptMoney(row.net_amount, currency)}</td>
                      <td>{formatReceiptMoney(row.vat_amount, currency)}</td>
                      <td>{formatReceiptMoney(row.gross_amount, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <section className="tax-invoice__payments">
          <p className="tax-invoice__section-title">Payment</p>
          {(invoice.payments ?? []).map((payment, index) => (
            <Row
              key={`${payment.method_code ?? payment.method_name}-${index}`}
              label={payment.reference ? `${payment.method_name} (${payment.reference})` : payment.method_name ?? 'Payment'}
              value={formatReceiptMoney(payment.amount, currency)}
            />
          ))}
        </section>

        {invoice.footer ? (
          <footer className="tax-invoice__footer">{invoice.footer}</footer>
        ) : null}
      </article>
    </div>
  )
}

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.value
 * @param {boolean} [props.bold]
 */
function Row({ label, value, bold = false }) {
  return (
    <div className={`tax-invoice__row${bold ? ' tax-invoice__row--bold' : ''}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}
